// api/api.ts
// Клиент Django-бэка с сессионной аутентификацией.
// Под капотом /user/profile/ маппится в DashboardResponse — компоненты пользуются
// только им и не знают про реальные имена полей Django.

import axios, { AxiosInstance } from "axios";
import { AuthResponse, DashboardResponse } from "../types/types";

// Базовый URL API:
//   - NEXT_PUBLIC_API_URL — из .env.local (если фронт и API на разных доменах);
//   - "/api" — относительный путь, когда nginx на том же домене проксирует /api → Django.
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "/api";

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  // Cookie сессии (sessionid) уходит/возвращается;
  // на бэке должен быть CORS_ALLOW_CREDENTIALS=True и origin фронта в CORS_ALLOWED_ORIGINS.
  withCredentials: true,
  timeout: 15000,
});

// ============ Внутренний тип реального ответа Django /user/profile/ ============
interface DjangoProfileResponse {
  userData: {
    id: number;
    login: string;
    fullName: string;
    wallet: string;
    rewardWallet: string;
    tokens: string;
    sharePercent: string;
    expectedProfit: string;
    balanceUpdatedAt: string | null;
  };
  globalData: {
    totalUserTokens: string;
    totalUsers: number;
    totalSubscriptions: string;
    distributionPercent: string;
    distributionPool: string;
    payoutDate: string | null;
  };
}

const num = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const mapDashboard = (raw: DjangoProfileResponse): DashboardResponse => {
  const u = raw.userData;
  const g = raw.globalData;
  return {
    full_name: u.fullName || null,
    wallet: u.wallet || null,
    payout_wallet: u.rewardWallet || null,
    token: {
      code: process.env.NEXT_PUBLIC_TOKEN_CODE ?? "PPT",
      title: process.env.NEXT_PUBLIC_TOKEN_TITLE ?? "PPT",
      contract_address: process.env.NEXT_PUBLIC_TOKEN_CONTRACT ?? null,
      network: "erc20",
      balance: num(u.tokens),
      updated_at: u.balanceUpdatedAt,
    },
    subscription_total: num(g.totalSubscriptions),
    distributable_pool: num(g.distributionPool),
    reward_pool_share: num(g.distributionPercent),
    user_share_percent: num(u.sharePercent),
    estimated_payout: num(u.expectedProfit),
    payout_currency: "₽",
    payout_date: g.payoutDate,
  };
};

// ============ Auth ============
export const authAPI = {
  // Django ждёт `username`, фронт у нас называет это поле `login` — мапим имя.
  login: (login: string, password: string) =>
    api.post<AuthResponse>("/auth/login/", { username: login, password }),

  logout: () => api.post<{ success: boolean }>("/auth/logout/"),

  me: () =>
    api.get<{ authenticated: boolean; user_id?: number; username?: string }>(
      "/auth/me/"
    ),
};

// ============ User (личный кабинет) ============
export const userAPI = {
  // Дашборд — ходим в /user/profile/ и приводим ответ к DashboardResponse.
  getDashboard: async () => {
    const r = await api.get<DjangoProfileResponse>("/user/profile/");
    return { data: mapDashboard(r.data) };
  },

  // Все правки идут одним эндпоинтом /user/me/, отдельные методы — для удобства.
  // После сохранения сразу перечитываем дашборд, чтобы UI отразил новое состояние
  // (особенно баланс — при смене EVM-кошелька он перезапрашивается с RPC).
  updatePayoutWallet: async (payoutWallet: string) => {
    await api.patch("/user/me/", { reward_wallet: payoutWallet });
    return userAPI.getDashboard();
  },

  updateWallet: async (wallet: string) => {
    await api.patch("/user/me/", { wallet });
    return userAPI.getDashboard();
  },

  updateFullName: async (fullName: string) => {
    await api.patch("/user/me/", { full_name: fullName });
    return userAPI.getDashboard();
  },
};

export default {
  auth: authAPI,
  user: userAPI,
};
